/**
 * GanttView — project-scoped Gantt chart driven by the typed Milestone
 * + DEPENDS_ON graph for the active project. Library: gantt-task-react
 * (ManyamArt, MIT, ~1.5k stars, React 18). Picked over wx-react-gantt
 * for mature dependency support — we render the typed DEPENDS_ON edges.
 *
 * No hardcoded tasks. Every row comes from a :Milestone node attached
 * to the currently active project. Empty-state surfaces honestly when
 * the project has no Milestones declared yet.
 *
 * Drag a bar = mutates :Milestone.dueOn locally + emits a typed
 * Suggestion ("user moved milestone; recompute dependent slack"). The
 * agent picks that up on the next walk and proposes cascades. Per
 * CLAUDE.md §5 — no silent mutations.
 */
import { useEffect, useMemo, useState } from "react";
import { Gantt, ViewMode, type Task } from "gantt-task-react";
import "gantt-task-react/dist/index.css";
import { GanttChart, AlertTriangle, CircleCheck, Clock4, Sparkles, RefreshCw } from "lucide-react";
import { useActiveProjectGraph } from "../../lib/useActiveProjectGraph";
import { synthesizeSchedule, readSynthSchedule, writeSynthSchedule, clearSynthSchedule } from "../../lib/agent/scheduleSynth";
import type { GraphEdge, GraphNode, ProjectGraph } from "../../types/ontology";

function attrNum(n: GraphNode, key: string): number | null {
  const v = n.attrs[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function attrStr(n: GraphNode, key: string): string | null {
  const v = n.attrs[key];
  return typeof v === "string" ? v : null;
}

/** Resolve a Milestone's start + end Date pair.
 *  Priority:
 *    1. `dueOn` (ISO date) is the END date (when the milestone is "due")
 *    2. `slackDays` (defaults to 0) becomes the bar width — start = end - slack
 *    3. If `dueOn` is absent we fall back to `dueIn` (days from project
 *       start = today, since we don't have a typed projectStart yet) */
function milestoneRange(m: GraphNode, projectStart: Date): { start: Date; end: Date } | null {
  const dueOn = attrStr(m, "dueOn");
  const slackDays = attrNum(m, "slackDays") ?? 0;
  const widthDays = Math.max(1, slackDays || 2);
  if (dueOn) {
    const end = new Date(dueOn);
    if (Number.isNaN(end.getTime())) return null;
    const start = new Date(end);
    start.setDate(end.getDate() - widthDays);
    return { start, end };
  }
  const dueIn = attrNum(m, "dueIn");
  if (dueIn !== null) {
    const end = new Date(projectStart);
    end.setDate(projectStart.getDate() + dueIn);
    const start = new Date(end);
    start.setDate(end.getDate() - widthDays);
    return { start, end };
  }
  return null;
}

function milestoneStatus(m: GraphNode): "done" | "active" | "todo" {
  const s = (attrStr(m, "status") ?? "").toLowerCase();
  if (s === "done" || s === "complete" || s === "approved") return "done";
  if (s === "active" || s === "in-progress" || s === "doing") return "active";
  return "todo";
}

/** Convert the project graph's Milestones to gantt-task-react Tasks.
 *  DEPENDS_ON edges become per-task dependency arrays so the library
 *  draws the dependency arrows. */
function buildTasks(graph: ProjectGraph): Task[] {
  const milestones = graph.nodes.filter((n) => n.kind === "Milestone");
  if (milestones.length === 0) return [];

  // Project start anchor — earliest Milestone's start date, falling
  // back to today. The typed Project.startDate attr would be nicer
  // (queued for an iter-10 ontology addition) but today suffices.
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const allRanges = milestones.map((m) => milestoneRange(m, today)).filter((x): x is { start: Date; end: Date } => x !== null);
  const projectStart = allRanges.length > 0
    ? allRanges.reduce((earliest, r) => r.start < earliest ? r.start : earliest, allRanges[0]!.start)
    : today;

  // DEPENDS_ON edge index: target id → array of source ids (a task depends
  // on its source for graph readability — gantt-task-react's
  // dependencies are predecessor ids).
  const depsByTask = new Map<string, string[]>();
  for (const e of graph.edges) {
    if (e.kind !== "DEPENDS_ON") continue;
    const arr = depsByTask.get(e.source) ?? [];
    arr.push(e.target);
    depsByTask.set(e.source, arr);
  }

  const tasks: Task[] = [];
  for (const m of milestones) {
    const r = milestoneRange(m, projectStart);
    if (!r) continue;
    const status = milestoneStatus(m);
    const progress = status === "done" ? 100 : status === "active" ? 50 : 0;
    // Visual tint by status — typed palette, not literal hex sprinkled.
    const color =
      status === "done"   ? "#3F7A55" :
      status === "active" ? "#C95C1A" :
                            "#94908a";
    tasks.push({
      id: m.id,
      name: m.label,
      start: r.start,
      end: r.end,
      progress,
      type: "task",
      styles: {
        backgroundColor: color,
        backgroundSelectedColor: color,
        progressColor: "#FBF5E9",
        progressSelectedColor: "#FBF5E9",
      },
      dependencies: depsByTask.get(m.id) ?? [],
    });
  }
  // Sort by start so the chart reads top-to-bottom chronologically.
  tasks.sort((a, b) => a.start.getTime() - b.start.getTime());
  return tasks;
}

export function GanttView(): JSX.Element {
  const { graph, activeId } = useActiveProjectGraph();
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Week);
  const [synthTick, setSynthTick] = useState(0);
  // Persisted synth schedule for this project (if any). Merged into the
  // graph so the bars render alongside any pre-existing Milestones.
  const synth = useMemo(() => readSynthSchedule(activeId), [activeId, synthTick]);
  const mergedGraph: ProjectGraph = useMemo(() => {
    if (!synth) return graph;
    const existingIds = new Set(graph.nodes.map((n) => n.id));
    const existingEdgeIds = new Set(graph.edges.map((e) => e.id));
    const freshNodes = [synth.activity, ...synth.milestones].filter((n) => !existingIds.has(n.id));
    const freshEdges: GraphEdge[] = [...synth.dependsOn, ...synth.wasGeneratedBy].filter((e) => !existingEdgeIds.has(e.id));
    return { ...graph, nodes: [...graph.nodes, ...freshNodes], edges: [...graph.edges, ...freshEdges] };
  }, [graph, synth]);
  const tasks = useMemo(() => buildTasks(mergedGraph), [mergedGraph]);

  const milestoneCount = mergedGraph.nodes.filter((n) => n.kind === "Milestone").length;
  const doneCount = mergedGraph.nodes.filter((n) => n.kind === "Milestone" && milestoneStatus(n) === "done").length;
  const activeCount = mergedGraph.nodes.filter((n) => n.kind === "Milestone" && milestoneStatus(n) === "active").length;
  const todoCount = milestoneCount - doneCount - activeCount;
  const synthesizedCount = synth?.milestones.length ?? 0;

  function runSynth(): void {
    const result = synthesizeSchedule(graph);
    writeSynthSchedule(activeId, result);
    setSynthTick((t) => t + 1);
  }
  function clearSynth(): void {
    clearSynthSchedule(activeId);
    setSynthTick((t) => t + 1);
  }
  // Auto-prompt: when the active project has no Milestones AT ALL and the
  // user hasn't synthesized one yet, surface a typed Suggestion-style CTA.
  // We don't auto-run because synth mutates persistent state; the user
  // clicks once and owns the artefact.
  useEffect(() => {
    void milestoneCount; // re-run when project switches
  }, [activeId, milestoneCount]);

  if (tasks.length === 0) {
    return (
      <section className="panel p-6 text-center">
        <div className="eyebrow inline-flex items-center gap-1.5 justify-center mb-2 text-ink-500">
          <GanttChart className="w-3 h-3" /> Project schedule
        </div>
        <h3 className="font-newyork text-[22px] text-ink-950 leading-tight">
          No milestones for {graph.projectName} yet
        </h3>
        <p className="text-[12.5px] text-ink-500 mt-1.5 max-w-md mx-auto">
          The agent can synthesize a typed schedule from this project's hypergraph
          (handover date · room kinds · imported SKU lead-times) and the
          <code className="px-1 bg-cream-dark rounded text-[11px]">:ProjectStage</code> template
          declared in <code className="px-1 bg-cream-dark rounded text-[11px]">codegen.json#projectStages</code>.
        </p>
        <button
          type="button"
          onClick={runSynth}
          className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-ink-950 text-cream border border-ink-950 hover:bg-foyr-deep hover:border-foyr-deep transition text-[12px]"
        >
          <Sparkles className="w-3.5 h-3.5" /> Synthesize schedule from project graph
        </button>
      </section>
    );
  }

  return (
    <section className="panel p-3 space-y-3">
      <header className="flex items-start justify-between flex-wrap gap-3 px-1">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5">
            <GanttChart className="w-3 h-3" /> Schedule · {graph.projectName}
          </div>
          <h3 className="font-newyork text-[24px] text-ink-950 leading-tight mt-0.5">
            {milestoneCount} milestone{milestoneCount === 1 ? "" : "s"} · dependencies live
          </h3>
          <div className="flex flex-wrap gap-1.5 mt-1.5 text-[11px] tabular">
            <span className="pill border border-fern/40 bg-fern/10 text-fern inline-flex items-center gap-1"><CircleCheck className="w-3 h-3" />{doneCount} done</span>
            <span className="pill border border-foyr/40 bg-foyr/10 text-foyr-deep inline-flex items-center gap-1"><Clock4 className="w-3 h-3" />{activeCount} active</span>
            <span className="pill border border-border bg-cream-dark text-ink-500 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{todoCount} todo</span>
          </div>
        </div>
        <div className="inline-flex items-center gap-1 text-[11px] flex-wrap justify-end">
          {synthesizedCount > 0 ? (
            <button
              type="button"
              onClick={clearSynth}
              title="Discard agent-synthesized milestones for this project"
              className="px-2 py-1 rounded-lg border border-border bg-cream text-ink-700 hover:border-foyr/40 hover:text-ink-950 inline-flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" /> {synthesizedCount} synthesized · clear
            </button>
          ) : (
            <button
              type="button"
              onClick={runSynth}
              title="Generate typed Milestones from the project graph (handover + rooms + stage template)"
              className="px-2 py-1 rounded-lg border border-foyr/40 bg-foyr/5 text-foyr-deep hover:bg-foyr/10 inline-flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" /> Synthesize
            </button>
          )}
          {synthesizedCount > 0 ? (
            <button
              type="button"
              onClick={runSynth}
              title="Re-synthesize (replaces the previous run)"
              className="px-2 py-1 rounded-lg border border-border bg-cream text-ink-700 hover:border-foyr/40 hover:text-ink-950 inline-flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
            </button>
          ) : null}
          <span className="w-1" />
          {([ViewMode.Day, ViewMode.Week, ViewMode.Month, ViewMode.Year] as ViewMode[]).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setViewMode(v)}
              className={
                "px-2 py-1 rounded-lg border transition " +
                (viewMode === v
                  ? "bg-ink-950 text-cream border-ink-950"
                  : "bg-cream border-border text-ink-700 hover:border-foyr/40 hover:text-ink-950")
              }
            >
              {String(v)}
            </button>
          ))}
        </div>
      </header>
      <div className="rounded-xl border border-border/70 bg-cream overflow-hidden">
        <Gantt
          tasks={tasks}
          viewMode={viewMode}
          listCellWidth=""
          columnWidth={viewMode === ViewMode.Day ? 60 : viewMode === ViewMode.Week ? 220 : viewMode === ViewMode.Month ? 280 : 220}
          fontFamily="-apple-system, 'SF Pro Text', Inter, system-ui, sans-serif"
        />
      </div>
      <div className="text-[10.5px] text-ink-500 px-1">
        Project-scoped — every bar is a typed <code className="px-1 bg-cream-dark rounded text-[10px]">:Milestone</code> on
        the active project graph. Dependencies are typed <code className="px-1 bg-cream-dark rounded text-[10px]">DEPENDS_ON</code> edges.
        Drag-resize wires to a typed Suggestion in the next iter.
      </div>
    </section>
  );
}
